import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './VisitorSurvey.css';

const STORAGE_KEY = 'otta_visitor_survey_v1';
const SHOW_AFTER_MS = 30000;

const REASONS = [
    'Buying a home',
    'Selling a home',
    'Just curious about prices',
    'Estate agent / property professional',
    'Developer / data or API',
    'Moving Home with Charlie / BestAgent',
    'Something else',
];

const SOURCES = [
    'Search engine',
    'Social media',
    'An AI assistant',
    'A friend or colleague',
    'Article or link',
    'Other',
];

// localStorage can throw in private windows; treat failures as "not seen".
const alreadySeen = () => {
    try {
        return Boolean(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
        return false;
    }
};

const markSeen = (value) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
    }
};

const postSurvey = (answers) => {
    const base = process.env.REACT_APP_API_URL;
    if (!base) return;
    // Fire and forget: a failed survey post should never surface to the visitor.
    fetch(`${base.replace(/\/$/, '')}/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
        keepalive: true,
    }).catch(() => {});
};

/**
 * Small corner prompt asking anonymous and signed-in visitors what brought them here.
 * Shown once per browser, after SHOW_AFTER_MS on the site. Answers go to POST /survey.
 */
function VisitorSurvey() {
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [step, setStep] = useState(1);
    const [reason, setReason] = useState('');
    const [source, setSource] = useState('');
    const [comment, setComment] = useState('');
    const sent = useRef(false);

    useEffect(() => {
        if (alreadySeen()) return undefined;
        const timer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
        return () => clearTimeout(timer);
    }, []);

    const send = () => {
        if (sent.current || !(reason || source || comment.trim())) return;
        sent.current = true;
        postSurvey({
            reason,
            source,
            comment: comment.trim(),
            page: location.pathname,
            referrer: document.referrer,
        });
    };

    const handleClose = () => {
        // Keep a first answer even if they close before finishing.
        send();
        markSeen(sent.current ? 'answered' : 'dismissed');
        setVisible(false);
    };

    const handleReason = (value) => {
        setReason(value);
        setStep(2);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        send();
        markSeen('answered');
        setStep(3);
        setTimeout(() => setVisible(false), 2000);
    };

    if (!visible) return null;

    return (
        <div className="visitor-survey" role="dialog" aria-label="Quick question">
            <button type="button" className="visitor-survey-close" onClick={handleClose} aria-label="Close">
                &times;
            </button>

            {step === 1 && (
                <>
                    <p className="visitor-survey-question">What brought you here today?</p>
                    <div className="visitor-survey-options">
                        {REASONS.map((option) => (
                            <button
                                type="button"
                                key={option}
                                className={`visitor-survey-option${reason === option ? ' selected' : ''}`}
                                onClick={() => handleReason(option)}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {step === 2 && (
                <form onSubmit={handleSubmit}>
                    <p className="visitor-survey-question">How did you find us?</p>
                    <div className="visitor-survey-options">
                        {SOURCES.map((option) => (
                            <button
                                type="button"
                                key={option}
                                className={`visitor-survey-option${source === option ? ' selected' : ''}`}
                                onClick={() => setSource(option)}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                    <textarea
                        className="visitor-survey-comment"
                        placeholder="Anything you were hoping to find? (optional)"
                        value={comment}
                        maxLength={500}
                        rows={2}
                        onChange={(e) => setComment(e.target.value)}
                    />
                    <div className="visitor-survey-actions">
                        {/* Nothing is posted until Send or close, so going back can still change the reason. */}
                        <button type="button" className="visitor-survey-back" onClick={() => setStep(1)}>
                            &larr; Back
                        </button>
                        <button type="submit" className="otta-button visitor-survey-send">Send</button>
                    </div>
                </form>
            )}

            {step === 3 && (
                <p className="visitor-survey-question visitor-survey-thanks">Thanks, that really helps.</p>
            )}
        </div>
    );
}

export default VisitorSurvey;
